import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';

import hooks from '@g4/automation-hooks';

import { Box, ConditionalWrap } from '../Boneyard';

export const propTypes = {
  /** Textarea field `name` attribute value */
  name: PropTypes.string,
  /** The value */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** The default value */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Text to display whilst a value hasn't been selected */
  placeholder: PropTypes.string,
  /** Display a border around the textarea */
  withBorder: PropTypes.bool,
  /** Prevents text from being entered */
  disabled: PropTypes.bool,
  /** Applies error styling */
  hasError: PropTypes.bool,
  /** A ref to the <textarea> element */
  inputRef: PropTypes.object,
  /** onchange callback handler */
  onChange: PropTypes.func,
  /** onblur callback handler */
  onBlur: PropTypes.func,
  /** onfocus callback handler */
  onFocus: PropTypes.func,
  /** The maximum number of characters allowed to be entered */
  maxLength: PropTypes.number,
  /** Prevents users from modifying the field value */
  readOnly: PropTypes.bool,
  /** A regular expression the input's contents must match in order to be valid */
  pattern: PropTypes.string,
  /** Associates a specific action onto an element for the benefit of automated clients consuming it */
  automationHook: PropTypes.string,
  theme: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.node,
};

export const defaultProps = {
  withBorder: true,
  onChange: Function.prototype,
  onBlur: Function.prototype,
};

const ClickableWrapper = styled.div`
  cursor: ${props => (props.disabled ? 'initial' : 'pointer')};
`;

const StyledTextarea = styled.textarea`
  resize: none;
  font-size: 16px;
`;

const Textarea = styled(
  withTheme(props => (
    <Box position="relative">
      <ConditionalWrap
        condition={props.onClick}
        wrap={children => (
          <ClickableWrapper onClick={props.onClick} disabled={props.disabled}>
            {children}
          </ClickableWrapper>
        )}
      >
        <StyledTextarea
          ref={props.inputRef}
          name={props.name}
          id={props.id}
          value={props.value}
          defaultValue={props.defaultValue}
          placeholder={props.placeholder}
          disabled={props.disabled}
          maxLength={props.maxLength}
          pattern={props.pattern}
          readOnly={props.readOnly}
          autoComplete={props.autoComplete}
          onChange={event => props.onChange(event.target.value)}
          onBlur={event => props.onBlur(event.target.value)}
          onFocus={props.onFocus}
          className={props.className}
          {...hooks.hook(props.automationHook)}
          tabIndex={props.onClick ? '-1' : '0'}
        />
      </ConditionalWrap>

      {props.children}
    </Box>
  ))
)`
  height: 75px;
  width: 100%;
  display: flex;
  align-items: center;
  padding: ${props => props.theme.spacing(2)};
  border-radius: 8px;
  text-overflow: ellipsis;
  font-family: 'Montserrat', sans-serif;

  ${props =>
    props.disabled &&
    `
    color: ${props.theme.colors.input.disabledText};
    background-color: ${props.theme.colors.input.disabledBackground};
  `}

  &:hover,
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.theme.colors.input.focused};
  }

  ${props =>
    props.withBorder &&
    `
    border: 1px solid ${props.theme.colors.input.border};
  `}

  ${props =>
    props.hasError &&
    !props.disabled &&
    `
    border-color: ${props.theme.colors.input.error};
  `}

  &::placeholder {
    color: ${props => props.theme.colors.input.placeholderText};
  }
`;

Textarea.propTypes = propTypes;
Textarea.defaultProps = defaultProps;

export default Textarea;
